import keys from '@debbie/common/dist/cache/keys.js';
import { FeatureFlag } from '@debbie/common/dist/feature-flags/index.js';
import { Entity, Site } from '@debbie/common/dist/index.js';
import 'react-select-v1/dist/react-select.css';
import { Direction, IncomingState, InteractionType, OutgoingState, } from '@debbie/common/dist/interactions/index.js';
import { PerformedByType, } from '@debbie/common/dist/performed-by/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import * as aiAPI from '@debbie/cortex/dist/api/ai.js';
import * as interactionCategoriesApi from '@debbie/cortex/dist/api/interaction-categories.js';
import * as interactionsAPI from '@debbie/cortex/dist/api/interactions.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useInteractionCategories } from '@debbie/cortex/dist/interaction-categories/index.js';
import { interactionsByReferenceKey, interactionsEntity, } from '@debbie/cortex/dist/interactions/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { notification } from '@debbie/cortex/dist/notifications.js';
import q from '@debbie/cortex/dist/query.js';
import { session } from '@debbie/cortex/dist/sessions.js';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { useVariation } from '../../feature-flags/index.js';
import { searchAll } from '../../search/index.js';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import AI from '../AI.js';
import Badge from '../Badge.js';
import Button from '../Button.js';
import Clock from '../Icons/Clock.js';
import EyeOff from '../Icons/EyeOff.js';
import { CornerUpLeft, Plus, X } from '../Icons/index.js';
import InteractionSummary from '../Interaction/Interaction.js';
import Loading from '../Loading.js';
import Modal from '../Modal.js';
import PerformedBy from '../performed-by/PerformedBy.js';
import SearchInput from '../SearchInput.js';
import HistItem from './HistItem.js';
const InteractionItem = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const role = useRole();
    const hasAIEnabled = useVariation(FeatureFlag.AISuggestions);
    const { historyItem: { data }, referenceId, } = props;
    const { direction, transacted, templateId } = data;
    const isOutgoing = data.direction === Direction.OUTGOING;
    const isIncoming = data.direction === Direction.INCOMING;
    const isPending = data.state === OutgoingState.PENDING;
    const isSent = data.state === OutgoingState.SENT;
    const isMapped = data.state === IncomingState.MAPPED;
    const isPhone = data.type === InteractionType.Phone;
    const isClientNote = data.type === InteractionType.ClientNote;
    const isDebtorNote = data.type === InteractionType.DebtorNote;
    const isNote = data.type === InteractionType.Note;
    const isEmail = data.type === InteractionType.Email;
    const isLetter = data.type === InteractionType.Letter;
    // Direction is stored relative to the collector, but messages are labeled
    // from the viewer's own perspective: the caseworker sees them as from/to the
    // creditor/debtor, while the creditor/debtor sees the same message as
    // to/from the collector.
    const viewerIsCounterparty = (isClientNote && site === Site.Client) ||
        (isDebtorNote && site === Site.Debtor);
    const byDirection = (incoming, outgoing) => isIncoming ? incoming : outgoing;
    const messageHeading = !(isClientNote || isDebtorNote)
        ? null
        : viewerIsCounterparty
            ? byDirection(lang.interactions.messageToCollector, lang.interactions.messageFromCollector)
            : isClientNote
                ? byDirection(lang.interactions.messageFromCreditor, lang.interactions.messageToCreditor)
                : byDirection(lang.interactions.messageFromDebtor, lang.interactions.messageToDebtor);
    const visibleToClients = ['client', 'debtor'].includes(data.minVisibilityRole);
    const options = [];
    const [showThread, setShowThread] = useState(false);
    const [replying, setReplying] = useState(false);
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const invalidateInteractions = () => {
        if (referenceId) {
            q.invalidate([
                interactionsByReferenceKey(props.referenceType, referenceId),
            ]);
        }
    };
    const deleteMutation = useMutation(async () => {
        await interactionsAPI.del(data.id);
    }, {
        onSuccess: () => {
            invalidateInteractions();
            session.createEvent('deleted_interaction', {
                interactionId: data.id,
                interactionType: data.type,
            });
        },
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.interaction);
        },
    });
    const unmapMutation = useMutation(() => interactionsAPI.patch(data.id, {
        referenceId: null,
        referenceType: null,
        state: IncomingState.UNMAPPED,
    }), {
        onSuccess: invalidateInteractions,
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.interaction);
        },
    });
    const toggleVisibilityMutation = useMutation(async () => {
        if (!referenceId) {
            return;
        }
        await interactionsAPI.patch(data.id, {
            minVisibilityRole: visibleToClients ? 'caseworker' : 'client',
        });
    }, {
        onSuccess: invalidateInteractions,
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.interaction);
        },
    });
    const generateReplyMutation = useMutation(async () => {
        return await aiAPI.triggerAutoReply(data.id);
    }, {
        onSuccess: (result) => {
            invalidateInteractions();
            if (result.blocked) {
                const reasonKey = result.blockedReason?.split(':')?.[0]?.trim();
                const blockedReasonText = (reasonKey != null
                    ? lang.autoReply.blockedReasons[reasonKey]
                    : undefined) ?? result.blockedReason;
                notification({
                    title: lang.interactions.replyBlocked,
                    subtitle: blockedReasonText,
                    theme: 'warning',
                });
            }
            else {
                notification({
                    title: lang.interactions.replyGenerated,
                    theme: 'success',
                });
            }
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.interaction);
        },
    });
    if (((isPending && isOutgoing) ||
        (isClientNote && isOutgoing) ||
        isPhone ||
        isNote ||
        (role && isAdmin(role))) &&
        site === Site.Caseworker) {
        options.push({
            label: lang.interactions.delete,
            onClick: () => deleteMutation.mutate(),
        });
    }
    if (templateId) {
        options.push({
            label: lang.interactions.editTemplate,
            onClick: () => {
                window.open(`/settings/template-editor/${templateId}`, '_blank');
            },
        });
    }
    if (isIncoming && isMapped && site === Site.Caseworker) {
        options.push({
            label: lang.interactions.unmap,
            onClick: () => unmapMutation.mutate(),
        });
    }
    // Generate AI reply option for incoming mapped emails
    if (isIncoming &&
        isMapped &&
        isEmail &&
        hasAIEnabled &&
        props.referenceType === Entity.Case &&
        site === Site.Caseworker) {
        options.push({
            label: generateReplyMutation.isLoading
                ? lang.interactions.generatingReply
                : lang.interactions.generateReply,
            onClick: () => {
                if (!generateReplyMutation.isLoading) {
                    generateReplyMutation.mutate();
                }
            },
        });
    }
    if (!isClientNote && site === Site.Caseworker) {
        options.push({
            label: visibleToClients
                ? lang.interactions.hideFromClient
                : lang.interactions.showToClient,
            onClick: () => toggleVisibilityMutation.mutate(),
        });
    }
    if (site === Site.Caseworker) {
        options.push({
            label: lang.interactions.clone,
            onClick: () => {
                setCloneModalOpen(true);
            },
        });
    }
    const emailPrintFrameRef = useRef(null);
    if (isEmail) {
        options.push({
            label: `${lang.interactions.printPDF}`,
            onClick: async () => {
                emailPrintFrameRef.current = document.createElement('iframe');
                document.body.appendChild(emailPrintFrameRef.current);
                const contentWindow = emailPrintFrameRef.current.contentWindow;
                const doc = contentWindow.document;
                emailPrintFrameRef.current.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-modals');
                emailPrintFrameRef.current.setAttribute('src', 'about:blank');
                const email = data.details;
                doc.open();
                doc.write(`<div>
          <style>body { overflow: hidden; } img { max-height: 200px} @media print {
            @page {
                margin-top: 0;
                margin-bottom: 0;
            }
            body {
                padding-top: 72px;
                padding-bottom: 72px ;
            }
          }</style>
            <span><b>${lang.interactions.from}</b>: ${email.from}</span>
            <hr />
            <span><b>${lang.interactions.sent}</b>: ${moment(data.transacted || data.createdAt).format(THEME.DATE_FORMAT_LONG)}</span>
            <hr />
            <span><b>${lang.interactions.to}</b>: ${email.to}</span>
            <hr />
            <span><b>${lang.interactions.subject}</b>: ${email.subject}</span>
            <hr />
            ${email.html || email.body}
            </div>`);
                doc.close();
                const height = doc?.body?.scrollHeight;
                if (height && emailPrintFrameRef && emailPrintFrameRef.current) {
                    emailPrintFrameRef.current.style.height = `${height}px`;
                }
                emailPrintFrameRef.current.contentWindow?.print();
            },
        });
        options.push({
            label: lang.interactions.thread,
            onClick: () => {
                setShowThread(true);
            },
        });
    }
    const showInteractionCategories = site === Site.Caseworker &&
        hasAIEnabled &&
        data.direction === Direction.INCOMING &&
        (data.type === InteractionType.DebtorNote ||
            data.type === InteractionType.Email);
    return (React.createElement(FocusedPanel, { $panelId: props.historyItem.focused === true ? `interaction-${data.id}` : null },
        React.createElement(HistItem, { date: props.historyItem.date, timespan: props.historyItem.timespan, options: options, heading: React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingRight: '0.2em',
                    /*alignItems: 'center',*/
                } },
                site === Site.Debtor ? (React.createElement("span", null, messageHeading ?? (React.createElement(React.Fragment, null,
                    lang.interactions[direction === Direction.OUTGOING //Needs to be flipped on debtorweb
                        ? Direction.INCOMING
                        : Direction.OUTGOING],
                    ' ',
                    lang.interactions.debtorInteraction)))) : (React.createElement("div", null,
                    React.createElement("span", null,
                        data.createdBy ? (React.createElement(React.Fragment, null,
                            React.createElement(PerformedBy, { performedBy: data.createdBy }),
                            ' ')) : null,
                        messageHeading ? (React.createElement(React.Fragment, null,
                            messageHeading,
                            " ")) : (React.createElement(React.Fragment, null,
                            !isNote && lang.interactions[direction],
                            ' ',
                            lang.interactionTypes[data.type],
                            ' ')),
                        (isEmail || isLetter) && isOutgoing ? (isSent ? (React.createElement("span", { style: {
                                fontSize: '.8em',
                            } },
                            lang.interactions.SENT,
                            isEmail &&
                                transacted &&
                                ` ${lang.interactions.at} ${moment(transacted).format(THEME.DATE_FORMAT_LONG)}`)) : (isPending && React.createElement(Clock, null))) : (''),
                        isIncoming && isEmail && transacted ? (React.createElement("span", { style: {
                                fontSize: '.8em',
                            } },
                            ' ',
                            lang.interactions.journalizedAt,
                            ' ',
                            moment(transacted).format(THEME.DATE_FORMAT_LONG))) : null,
                        site === Site.Caseworker && !visibleToClients && (React.createElement(EyeOff, { style: { marginLeft: 5 }, "data-tooltip-content": lang.interactions.hiddenFromClient, "data-tooltip-id": "tooltip-default" })),
                        showInteractionCategories ? (React.createElement(Categories, { interaction: data })) : null,
                        site === Site.Caseworker && data.tags.length > 0 && (React.createElement(Tags, { tags: data.tags })),
                        generateReplyMutation.isLoading && (React.createElement(AILoadingBadge, null,
                            React.createElement(Loading, { width: "1em" }),
                            lang.interactions.generatingReply))))),
                isEmail && (React.createElement(Reply, { "data-tooltip-id": "tooltip-default", "data-tooltip-content": lang.interactions.reply, onClick: () => {
                        setReplying(true);
                    } }))) },
            React.createElement(InteractionSummary, { interaction: data, longCaseId: props.referenceType === Entity.Case ? props.referenceId : undefined, showThread: showThread, setShowThread: setShowThread, replying: replying, setReplying: setReplying })),
        cloneModalOpen && (React.createElement(Modal, { maxWidth: "500px", onToggle: () => setCloneModalOpen(false), header: lang.interactions.clone },
            React.createElement(CloneModal, { interactionId: data.id, closeModal: () => setCloneModalOpen(false) })))));
};
// The tenant's own labels on an interaction. Read-only: tags are applied by
// imports and system notes, and a hidden one explains why an interaction the
// default view leaves out is on screen.
const Tags = (props) => (React.createElement("span", { style: {
        display: 'inline-flex',
        gap: '0.5em',
        flexWrap: 'wrap',
        fontWeight: 'normal',
    } }, props.tags.map((tag) => (React.createElement(Badge, { key: tag.id, style: { color: THEME.COLOR_PRIMARY, marginBottom: '0' }, theme: THEME.COLOR_SECONDARY }, tag.name)))));
const Categories = (props) => {
    const categoriesLoader = useInteractionCategories();
    const categories = h.hasData(categoriesLoader) ? categoriesLoader.data : [];
    const [selectorOpen, setSelectorOpen] = useState(false);
    const selectorRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectorRef.current &&
                !selectorRef.current.contains(event.target)) {
                setSelectorOpen(false);
            }
        };
        if (selectorOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectorOpen]);
    return (React.createElement("span", { style: {
            display: 'inline-flex',
            gap: '0.5em',
            flexWrap: 'wrap',
        } },
        props.interaction.interactionCategories.map((cat) => {
            return React.createElement(Category, { category: cat, interaction: props.interaction });
        }),
        categories.length > 0 ? (React.createElement(Badge, { style: {
                display: 'inline-flex',
                gap: '0.3em',
                alignItems: 'center',
                color: THEME.COLOR_PRIMARY,
                marginBottom: '0',
                cursor: 'pointer',
            }, theme: THEME.COLOR_SECONDARY, onClick: () => {
                setSelectorOpen((v) => !v);
            } },
            React.createElement(Plus, null))) : null,
        selectorOpen && (React.createElement("div", { style: {
                position: 'relative',
            } },
            React.createElement("div", { ref: selectorRef, style: {
                    position: 'absolute',
                    background: '#eee',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    boxShadow: '0px 1px 4px 1px rgba(0,0,0,0.3)',
                    zIndex: '10000',
                    transform: 'translateX(calc(-100% - 8px))',
                    top: '22px',
                } }, categories
                .filter((c) => {
                return (props.interaction.interactionCategories.find((cat) => cat.id === c.id) == null);
            })
                .map((c) => {
                return (React.createElement(CategorySelectorItem, { interaction: props.interaction, category: c, onSelect: () => {
                        setSelectorOpen(false);
                    } }));
            }))))));
};
const CategorySelectorItem = (props) => {
    const lang = useSelector((s) => s.lang);
    const applyCategory = useMutation(interactionCategoriesApi.applyToInteraction, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.interactionCategory);
        },
        onSuccess: (res, interactionId) => {
            // TODO: Mutate interactions cache
            q.invalidate(keys.write.modify(interactionsEntity, interactionId));
        },
    });
    return (React.createElement("div", { style: {
            background: '#FFF',
            padding: '0.5em 1em',
            fontSize: '0.8em',
            cursor: 'pointer',
        }, onClick: () => {
            applyCategory.mutate(props.interaction.id, props.category.id);
            props.onSelect();
            session.createEvent('categorized_message', {
                interactionId: props.interaction.id,
                category: props.category.id,
            });
        } }, props.category.name));
};
const Category = (props) => {
    const lang = useSelector((s) => s.lang);
    const categoriesLoader = useInteractionCategories();
    const categoriesLookup = objectBy(h.hasData(categoriesLoader) ? categoriesLoader.data : [], (c) => c.id);
    const cat = props.category;
    const removeCategory = useMutation(interactionCategoriesApi.removeFromInteraction, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.interactionCategory);
        },
        onSuccess: (res, interactionId) => {
            q.invalidate(keys.write.modify(interactionsEntity, interactionId));
        },
    });
    if (removeCategory.isSuccess) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("span", { style: { fontWeight: 'normal' } },
        React.createElement(Badge, { style: {
                display: 'inline-flex',
                gap: '0.3em',
                alignItems: 'center',
                color: THEME.COLOR_PRIMARY,
                marginBottom: '0',
            }, theme: THEME.COLOR_SECONDARY },
            cat.createdBy.type === PerformedByType.AI ? React.createElement(AI, { height: "1em" }) : null,
            categoriesLookup[cat.id]?.name ?? null,
            removeCategory.isLoading ? (React.createElement(Loading, { width: "1em" })) : (React.createElement(X, { style: {
                    cursor: 'pointer',
                }, onClick: () => {
                    removeCategory.mutate(props.interaction.id, cat.id);
                    session.createEvent('uncategorized_message', {
                        interactionId: props.interaction.id,
                        category: cat.id,
                    });
                } })))));
};
export const CloneModal = (props) => {
    const lang = useSelector((s) => s.lang);
    const role = useRole();
    const [toCase, setToCase] = useState(null);
    const mut = useMutation(() => {
        if (toCase == null) {
            throw new Error('No case selected');
        }
        return interactionsAPI.clone(props.interactionId, toCase.id);
    }, {
        onError: (e) => {
            notification({
                title: `${lang.misc.error} (${lang.interactions.clone})`,
                subtitle: e instanceof Error ? e.message : lang.misc.errorOccured,
                theme: 'error',
            });
        },
        onSuccess: (result) => {
            console.log(result);
            props.closeModal();
            notification({
                title: lang.interactions.cloned,
                button: {
                    label: lang.misc.goToCase,
                    callback: () => {
                        if (toCase) {
                            c.redirect.redirect(`/cases/${toCase.entity === Entity.Case ? toCase.caseSequentialId : ''}`);
                        }
                    },
                },
            });
        },
    });
    if (!role) {
        return null;
    }
    return (React.createElement(CloneWrapper, null,
        React.createElement(SearchInput, { searchDelay: 300, onSelect: setToCase, placeholder: lang.misc.search, searchAll: (q) => searchAll(lang, role, q, { entity: Entity.Case }), onRemoveStatic: () => setToCase(null), static: toCase?.entity === Entity.Case
                ? `${toCase.userNames} - #${toCase.caseSequentialId}`
                : undefined }),
        React.createElement(Button, { loading: mut.isLoading, disabled: toCase === null, onClick: () => {
                mut.mutate();
            } }, lang.misc.submit)));
};
const CloneWrapper = styled.div `
  display: flex;
  flex-direction: column;
`;
const Reply = styled(CornerUpLeft) `
  border-radius: 10000px;
  cursor: pointer;
  /*border: 1px solid black;
  padding: 0.1em;*/
`;
const AILoadingBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  margin-left: 0.5em;
  font-size: 0.85em;
  color: ${THEME.COLOR_PRIMARY};
`;
export default InteractionItem;
// Outlines the interaction's panel, inset on its rounded body like a focused proposal.
const FocusedPanel = styled.div `
  ${(p) => p.$panelId == null
    ? ''
    : `[id='${p.$panelId}'] > div > .body {
          outline: 2px solid ${THEME.COLOR_ACCENT};
          outline-offset: -2px;
        }`}
`;
//# sourceMappingURL=InteractionItem.js.map